import React, { useRef, useState } from 'react';
import { CheckCircle2, Circle, Pencil, Calendar, Tag, RotateCcw, Archive, ArchiveRestore, Repeat, Users, Trash2 } from 'lucide-react';
import './SwipeableActivityCard.css';

const CATEGORY_COLORS = {
  home: 'blue', errands: 'purple', finance: 'green',
  health: 'red', work: 'yellow', personal: 'default'
};

const PRIORITY_MAP = {
  high: { label: 'High', color: 'red' },
  medium: { label: 'Med', color: 'yellow' },
  low: { label: 'Low', color: 'blue' },
};

export default function SwipeableActivityCard({ activity, onToggle, onEdit, onToggleActive, onDelete, style, currentUser }) {
  const [isSwiping, setIsSwiping] = useState(false);
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const cardRef = useRef(null);
  const startX = useRef(0);
  const currentX = useRef(0);

  const isCompleted = activity.status === 'completed';
  const isInactive = activity.active === false;
  const prio = PRIORITY_MAP[activity.priority] || PRIORITY_MAP.medium;
  const catColor = CATEGORY_COLORS[activity.category] || 'default';
  const isRecurring = activity.isRecurring || false;

  // Permission: admin OR owner can act on this chore
  const canManage = currentUser && (
    currentUser.role === 'admin' ||
    String(activity.userId) === String(currentUser.id)
  );

  const isOverdue = activity.dueDate && !isCompleted && !isInactive &&
    new Date(activity.dueDate) < new Date(new Date().toDateString());

  const handleTouchStart = (e) => {
    if (!canManage) return;
    
    startX.current = e.touches[0].clientX;
    currentX.current = startX.current;
    setIsSwiping(true);
    
    if (cardRef.current) {
      cardRef.current.style.transition = 'none';
    }
  };

  const handleTouchMove = (e) => {
    if (!isSwiping || !cardRef.current) return;
    
    currentX.current = e.touches[0].clientX;
    const deltaX = currentX.current - startX.current;
    
    // Limit swipe distance
    const maxSwipe = 100;
    const clampedDelta = Math.max(-maxSwipe, Math.min(maxSwipe, deltaX));
    
    setSwipeOffset(clampedDelta);
    cardRef.current.style.transform = `translateX(${clampedDelta}px)`;
  };

  const handleTouchEnd = () => {
    if (!cardRef.current) return;
    
    const deltaX = currentX.current - startX.current;
    const threshold = 60; // Swipe threshold for action
    
    cardRef.current.style.transition = 'transform 0.3s ease';
    
    if (Math.abs(deltaX) > threshold) {
      if (deltaX < 0 && canManage) {
        // Swipe left - show delete action
        setSwipeOffset(-80);
        setTimeout(() => {
          if (onDelete) {
            onDelete(activity.id);
          }
        }, 200);
      } else if (deltaX > 0 && canManage) {
        // Swipe right - toggle complete/incomplete
        setSwipeOffset(0);
        onToggle(activity.id);
      }
    } else {
      // Snap back to center
      setSwipeOffset(0);
      cardRef.current.style.transform = 'translateX(0)';
    }
    
    setIsSwiping(false);
  };

  const handleDeleteClick = (e) => {
    e.stopPropagation();
    setIsDeleting(true);
    setTimeout(() => {
      if (onDelete) {
        onDelete(activity.id);
      }
    }, 300);
  };

  const handleToggleActive = () => {
    onToggleActive(activity.id);
  };

  return (
    <div className="swipeable-activity-card-container">
      {/* Delete action background */}
      <div className="swipe-delete-action">
        <Trash2 size={20} />
        <span>Delete</span>
      </div>
      
      {/* Main card */}
      <div
        ref={cardRef}
        className={`activity-card swipeable-card ${isCompleted ? 'card-completed' : ''} ${isOverdue ? 'card-overdue' : ''} ${isInactive ? 'card-inactive' : ''} ${isDeleting ? 'card-deleting' : ''}`}
        style={style}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Checkbox */}
        <button
          className="check-btn"
          onClick={() => canManage && onToggle(activity.id)}
          disabled={!canManage}
          title={canManage ? (isCompleted ? 'Mark as pending' : 'Mark as complete') : 'View only'}
          aria-label={canManage ? (isCompleted ? 'Mark as pending' : 'Mark as complete') : 'View only'}
        >
          {isCompleted
            ? <CheckCircle2 size={24} className="check-done" />
            : <Circle size={24} className="check-empty" />
          }
        </button>

        {/* Card content */}
        <div className="card-body">
          <div className="card-top">
            <span className={`card-title ${isCompleted ? 'title-done' : ''}`}>{activity.title}</span>
            <div className="card-badges">
              {isInactive && <span className="badge badge-inactive">Inactive</span>}
              {isRecurring && (
                <span className="badge badge-recurring" title={`Recurring ${activity.recurrenceType || 'weekly'}`}>
                  <Repeat size={10} />
                  Recurring
                </span>
              )}
              <span className={`badge badge-${prio.color}`}>{prio.label}</span>
              <span className={`badge badge-cat badge-${catColor}`}>
                <Tag size={10} />
                {activity.category}
              </span>
            </div>
          </div>

          {activity.notes && (
            <p className="card-notes">{activity.notes}</p>
          )}

          <div className="card-meta">
            {activity.dueDate && (
              <span className={`meta-date ${isOverdue ? 'overdue' : ''}`}>
                <Calendar size={12} />
                {isOverdue ? 'Overdue · ' : ''}
                {new Date(activity.dueDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              </span>
            )}
            {activity.assignedUsers && activity.assignedUsers.length > 0 && (
              <span className="meta-assigned" title={`Assigned to ${activity.assignedUsers.length} user(s)`}>
                <Users size={12} />
                {activity.assignedUsers.length} assigned
              </span>
            )}
            <span className="meta-created">
              Added {new Date(activity.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            </span>
          </div>
        </div>

        {/* Action buttons */}
        <div className="card-actions">
          {canManage && (
            <button
              className={`action-btn action-complete ${isCompleted ? 'is-active' : ''}`}
              onClick={() => onToggle(activity.id)}
              disabled={isInactive}
              title={isInactive ? 'Reactivate first to change status' : (isCompleted ? 'Reopen (mark as pending)' : 'Complete')}
              aria-label={isInactive ? 'Reactivate first to change status' : (isCompleted ? 'Reopen (mark as pending)' : 'Complete')}
            >
              {isCompleted ? <RotateCcw size={18} /> : <CheckCircle2 size={18} />}
            </button>
          )}
          <button
            className="action-btn"
            onClick={() => onEdit(activity)}
            disabled={isInactive}
            title={isInactive ? 'Reactivate first to edit' : 'Edit'}
            aria-label={isInactive ? 'Reactivate first to edit' : 'Edit'}
          >
            <Pencil size={18} />
          </button>
          {canManage && (
            <button
              className={`action-btn action-deactivate ${isInactive ? 'is-inactive' : ''}`}
              onClick={handleToggleActive}
              title={isInactive ? 'Reactivate' : 'Deactivate'}
              aria-label={isInactive ? 'Reactivate' : 'Deactivate'}
            >
              {isInactive ? <ArchiveRestore size={18} /> : <Archive size={18} />}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
